import { type LanguageCode as LibraryLanguage } from 'charting_library';

import { type IMediator } from '#/core/mediator';

import { SERVICES_TYPES } from '#/services-types';

import { type ILogService } from '../log';
import { type IUrlParamsService, UrlParamsName } from '../url-params';

import { type II18nService, SystemLanguage } from './i18n.types';

export class I18nService implements II18nService {
  readonly #serviceName = 'I18nService';

  get #urlParamsService() {
    return this._mediator.get<IUrlParamsService>(SERVICES_TYPES.UrlParamsService);
  }
  get #logService() {
    return this._mediator.get<ILogService>(SERVICES_TYPES.LogService);
  }

  readonly #defaultLanguage: LibraryLanguage = 'en';

  #availableLibraryLanguages: LibraryLanguage[] = [
    'ar',
    'zh',
    'ca_ES',
    'en',
    'fr',
    'de',
    'he_IL',
    'id_ID',
    'it',
    'ja',
    'ko',
    'pl',
    'pt',
    'ru',
    'es',
    'sv',
    'th',
    'tr',
    'vi',
    'ms_MY',
    'zh_TW',
  ];

  #availableSystemLanguages: SystemLanguage[] = [
    SystemLanguage.En,
    SystemLanguage.Es,
    SystemLanguage.Pt,
    SystemLanguage.Fr,
    SystemLanguage.Id,
    SystemLanguage.Ms,
    SystemLanguage.Cn,
    SystemLanguage.Zh,
    SystemLanguage.Ko,
    SystemLanguage.Vi,
    SystemLanguage.Th,
    SystemLanguage.Ar,
    SystemLanguage.Pk,
    SystemLanguage.Bn,
    SystemLanguage.Tr,
    SystemLanguage.Hi,
    SystemLanguage.Fa,
  ];

  // Missing library locales: pk, bn, hi, fa

  #mapSystemLanguageToLibrary: Partial<Record<SystemLanguage, LibraryLanguage>> = {
    id: 'id_ID',
    ms: 'ms_MY',
    cn: 'zh_TW',
    jp: 'ja',
  };

  #mapLibraryLanguageToSystem: Partial<Record<LibraryLanguage, SystemLanguage>> = Object.fromEntries(
    Object.entries(this.#mapSystemLanguageToLibrary).map(([key, value]) => [value, key])
  );

  get #providedLanguage() {
    const providedLanguage = this.#urlParamsService.getParamByName(UrlParamsName.Language);

    if (!providedLanguage) return;

    return providedLanguage;
  }

  get #browserLanguage() {
    // Normalize locale code, example en-US -> en
    return navigator.language.toLowerCase().split('-')[0];
  }

  constructor(private readonly _mediator: IMediator) {
    this.#logService.info(this.#serviceName, 'Instance was created.');
  }

  getLanguage(): LibraryLanguage {
    if (this.#providedLanguage) {
      this.#logService.info(this.#serviceName, 'Detect provided language.');

      const providedLanguageFromAvailableLibraryLanguages = this.#availableLibraryLanguages.find(
        (libraryLanguage) => libraryLanguage === this.#providedLanguage
      );

      if (providedLanguageFromAvailableLibraryLanguages) {
        this.#logService.info(this.#serviceName, 'Apply provided language as library language.');

        return providedLanguageFromAvailableLibraryLanguages;
      }

      if (this.#providedLanguage in this.#mapSystemLanguageToLibrary) {
        this.#logService.info(this.#serviceName, 'Apply mapped provided language.');

        return this.#mapSystemLanguageToLibrary[this.#providedLanguage as SystemLanguage] as LibraryLanguage;
      }
    }

    if (this.#availableSystemLanguages.includes(this.#browserLanguage as SystemLanguage)) {
      this.#logService.info(this.#serviceName, 'Detect browser language.');

      const browserLanguageFromAvailableLibraryLanguages = this.#availableLibraryLanguages.find(
        (libraryLanguage) => libraryLanguage === this.#browserLanguage
      );

      if (browserLanguageFromAvailableLibraryLanguages) {
        this.#logService.info(this.#serviceName, 'Apply browser language as library language.');

        // Set to URL search params invert by library/system language
        this.#urlParamsService.setParamByName(
          UrlParamsName.Language,
          this.#mapLibraryLanguageToSystem[this.#browserLanguage as LibraryLanguage] || this.#browserLanguage
        );

        return browserLanguageFromAvailableLibraryLanguages;
      }

      if (this.#browserLanguage in this.#mapSystemLanguageToLibrary) {
        this.#logService.info(this.#serviceName, 'Apply mapped browser language.');

        // Set to URL search params invert by library/system language
        this.#urlParamsService.setParamByName(
          UrlParamsName.Language,
          this.#mapLibraryLanguageToSystem[this.#browserLanguage as LibraryLanguage] || this.#browserLanguage
        );

        return this.#mapSystemLanguageToLibrary[this.#browserLanguage as SystemLanguage] as LibraryLanguage;
      }
    }

    this.#logService.info(this.#serviceName, 'Apply default language.');

    this.#urlParamsService.setParamByName(UrlParamsName.Language, this.#defaultLanguage);

    return this.#defaultLanguage;
  }
}
